import { useCallback, useEffect, useState } from "react";
import axiosInstance from "./axiosInstance";

type UseDevicesApiOptions = {
  limit?: number;
  offset?: number;
  status?: string;
  deviceName?: string;
};

type DevicesApiResult<T> = {
  devices: T[];
  counts?: {
    total?: number;
    running?: number;
    idle?: number;
    stopped?: number;
    inactive?: number;
    expired?: number;
    no_data?: number;
  };
};

export const useDevicesApi = <T = any>(options?: UseDevicesApiOptions) => {
  const [devices, setDevices] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<DevicesApiResult<T>["counts"]>();

  const fetchDevices = useCallback(
    async (override?: UseDevicesApiOptions) => {
      setLoading(true);
      try {
        const merged = {
          limit: options?.limit ?? 10,
          offset: options?.offset ?? 0,
          response_type: "new_response",
          status: options?.status,
          device_name: options?.deviceName?.trim() || undefined,
          ...override,
        };

        const params = {
          limit: merged.limit,
          offset: merged.offset,
          response_type: merged.response_type,
          status: merged.status || undefined,
          device_name:
            typeof merged.device_name === "string" && merged.device_name.trim()
              ? merged.device_name.trim()
              : undefined,
        };

        const response = await axiosInstance.get("/gpstracker/devicehandler/", {
          params,
        });

        if (response.data?.status && response.data?.responseData) {
          const responseData = response.data.responseData as DevicesApiResult<T>;
          setDevices(responseData.devices || []);
          setCounts(responseData.counts || undefined);
        } else {
          setDevices([]);
          setCounts(undefined);
        }
      } catch (error) {
        console.error("Failed to load devices:", error);
        setDevices([]);
        setCounts(undefined);
      } finally {
        setLoading(false);
      }
    },
    [options?.deviceName, options?.offset, options?.status, options?.limit]
  );

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  return { devices, loading, counts, refreshDevices: fetchDevices };
};
